import {
  getMorphemeCardStage,
  type MorphemeTileCard,
  useMorphemeListCardStagesQuery,
} from "@-/dev.improve.haniverse.lib.morpheme.stage";
import { keepPreviousData, useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useCallback, useEffect } from "react";
import type { BatchedGraphemeTileDataOptions } from "#use-batched-grapheme-tile-data";
import type { MorphemeGlyphTileStage } from "#morpheme-glyph-tile-ui";

/** The stage of one of `grapheme`'s study cards, `"unadded"` while that card is not in study. */
export type GetGraphemeCardStage = (
  grapheme: string,
  card: MorphemeTileCard,
) => MorphemeGlyphTileStage;

/**
 * The per-card counterpart of `useBatchedGraphemeTileData`'s stage, for a grid whose tiles each
 * stand for ONE of a grapheme's study cards: a level's tiles show the card that level teaches, so
 * a learned meaning never shades the tile of a reading that is still to come. Lifted once for the
 * whole set, gated and refreshed exactly like the tile data's stage read (same options).
 */
export function useBatchedGraphemeCardStages(
  graphemes: readonly string[],
  { enabled = true, refetchVersion }: BatchedGraphemeTileDataOptions = {},
): GetGraphemeCardStage {
  const { data: cardStagesByGrapheme, refetch } = useQuery({
    ...useMorphemeListCardStagesQuery(graphemes),
    enabled,
    placeholderData: keepPreviousData,
    refetchOnReconnect: enabled,
    refetchOnWindowFocus: enabled,
  });

  useEffect(() => {
    if (refetchVersion !== undefined && refetchVersion > 0) {
      // See `useBatchedGraphemeTileData`: one catch-up read when a gated grid is entered again.
      void refetch({ cancelRefetch: false });
    }
  }, [refetch, refetchVersion]);

  return useCallback(
    (grapheme, card) => getMorphemeCardStage(cardStagesByGrapheme?.[grapheme], card),
    [cardStagesByGrapheme],
  );
}
